import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Description as PdfIcon,
  Image as ImageIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import clientDashboardService from '@services/clientDashboardService';
import EmptyState from '@shared/EmptyState';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';

interface Document {
  id: string;
  filename: string;
  original_filename: string;
  mime_type: string;
  file_size: number;
  category: string;
  owner_type: string;
  owner_id: string;
  storage_key: string;
  uploaded_at: string;
  patientName?: string;
}

const ClientDocuments: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [patients, setPatients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('other');
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<'pdf' | 'image'>('pdf');

  useEffect(() => {
    loadData();
  }, [filterCategory]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [docsResponse, patientsResponse] = await Promise.all([
        clientDashboardService.getDocuments({
          category: filterCategory !== 'all' ? filterCategory : undefined
        }),
        clientDashboardService.getPatients()
      ]);
      
      // Extract data arrays from responses
      const docsData = Array.isArray(docsResponse) ? docsResponse : (docsResponse as any)?.data || [];
      const patientsData = Array.isArray(patientsResponse) ? patientsResponse : (patientsResponse as any)?.data || [];
      
      setDocuments(docsData);
      setPatients(patientsData);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Eroare la incarcarea documentelor');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validare dimensiune (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Fisierul nu poate depasi 10MB');
      return;
    }

    // Validare tip fisier
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setError('Doar fisiere PDF, JPG si PNG sunt permise');
      return;
    }

    setSelectedFile(file);
    setUploadDialogOpen(true);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setUploadProgress(0);
      setError(null);

      // Genereaza URL presigned
      const { upload_url, document_id, storage_key } = await clientDashboardService.generateUploadUrl({
        filename: selectedFile.name,
        mime_type: selectedFile.type,
        file_size: selectedFile.size,
        owner_type: selectedPatientId ? 'patient' : 'client',
        owner_id: selectedPatientId || undefined,
        category: selectedCategory
      });

      // Upload fisier la URL presigned (simulat - in productie foloseste S3/GCS)
      // Pentru demonstratie, simulam progress
      for (let i = 0; i <= 100; i += 10) {
        setUploadProgress(i);
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setUploadDialogOpen(false);
      setSelectedFile(null);
      setSelectedCategory('other');
      setSelectedPatientId('');
      
      // Reincarca documentele
      await loadData();
    } catch (err) {
      console.error('Error uploading document:', err);
      setError('Eroare la incarcarea documentului');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (docId: string, filename: string) => {
    try {
      const { download_url } = await clientDashboardService.getDownloadUrl(docId);
      
      // Descarca fisierul
      const link = document.createElement('a');
      link.href = download_url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading document:', err);
      setError('Eroare la descarcarea documentului');
    }
  };

  const handlePreview = async (docId: string, mimeType: string) => {
    try {
      const { download_url } = await clientDashboardService.getDownloadUrl(docId);
      setPreviewUrl(download_url);
      setPreviewType(mimeType.startsWith('image/') ? 'image' : 'pdf');
      setPreviewDialogOpen(true);
    } catch (err) {
      console.error('Error previewing document:', err);
      setError('Eroare la previzualizarea documentului');
    }
  };

  const handleDelete = async (docId: string) => {
    if (!window.confirm('Sigur doriti sa stergeti acest document?')) return;

    try {
      await clientDashboardService.deleteDocument(docId);
      await loadData();
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Eroare la stergerea documentului');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <ImageIcon color="primary" />;
    if (mimeType === 'application/pdf') return <PdfIcon color="error" />;
    return <PdfIcon color="action" />;
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      lab: 'Analize',
      prescription: 'Retete',
      invoice: 'Facturi',
      other: 'Altele'
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            📄 Documentele Mele
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Documente medicale, retete, analize si facturi
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          disabled
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Incarcare indisponibila
        </Button>
      </Box>

      {/* Filters */}
      <Box mb={3}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Categorie</InputLabel>
          <Select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            label="Categorie"
          >
            <MenuItem value="all">Toate</MenuItem>
            <MenuItem value="lab">Analize</MenuItem>
            <MenuItem value="prescription">Retete</MenuItem>
            <MenuItem value="invoice">Facturi</MenuItem>
            <MenuItem value="other">Altele</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        In aceasta versiune, owner-ul poate doar vizualiza, descarca si sterge documentele deja disponibile.
      </Alert>

      {/* Empty State */}
      {documents.length === 0 && (
        <EmptyState
          icon={<PdfIcon sx={{ fontSize: 64, color: 'text.secondary' }} />}
          title="Niciun document incarcat"
          description="Incarca primul tau document medical pentru a-l pastra organizat"
        />
      )}

      {/* Documents Table */}
      {documents.length > 0 && (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tip</TableCell>
                <TableCell>Nume Fisier</TableCell>
                <TableCell>Categorie</TableCell>
                <TableCell>Animal</TableCell>
                <TableCell>Dimensiune</TableCell>
                <TableCell>Data</TableCell>
                <TableCell align="right">Actiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell>{getFileIcon(doc.mime_type)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {doc.original_filename}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getCategoryLabel(doc.category)}
                      size="small"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {doc.patientName || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatFileSize(doc.file_size)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {format(new Date(doc.uploaded_at), 'dd MMM yyyy', { locale: ro })}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                      <Tooltip title="Previzualizare">
                        <IconButton
                          size="small"
                          onClick={() => handlePreview(doc.id, doc.mime_type)}
                          sx={{ color: 'info.main' }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Descarca">
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(doc.id, doc.original_filename)}
                          sx={{ color: 'primary.main' }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sterge">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(doc.id)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => !uploading && setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Incarca Document</Typography>
            <IconButton onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedFile && (
            <Box mb={3}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Fisier selectat:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {selectedFile.name} ({formatFileSize(selectedFile.size)})
              </Typography>
            </Box>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Categorie</InputLabel>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              label="Categorie"
              disabled={uploading}
            >
              <MenuItem value="lab">Analize</MenuItem>
              <MenuItem value="prescription">Retete</MenuItem>
              <MenuItem value="invoice">Facturi</MenuItem>
              <MenuItem value="other">Altele</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Animal (optional)</InputLabel>
            <Select
              value={selectedPatientId}
              onChange={(e) => setSelectedPatientId(e.target.value)}
              label="Animal (optional)"
              disabled={uploading}
            >
              <MenuItem value="">Niciun animal</MenuItem>
              {patients.map((patient) => (
                <MenuItem key={patient.id} value={patient.id}>
                  {patient.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {uploading && (
            <Box mt={3}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                Incarcare: {uploadProgress}%
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            Anuleaza
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || uploading}
            startIcon={<UploadIcon />}
          >
            Incarca
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog 
        open={previewDialogOpen} 
        onClose={() => setPreviewDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Previzualizare Document</Typography>
            <IconButton onClick={() => setPreviewDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {previewUrl && (
            <>
              {previewType === 'image' ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Preview"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    backgroundColor: '#f5f5f5'
                  }}
                />
              ) : (
                <iframe
                  src={previewUrl}
                  title="PDF Preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ClientDocuments;

